import { BatchAndExpiryManagementRowsData, orderStatusConfig } from "../../../types/types";
import { ChannelkartNetworkGet } from "../../../services/networkService";
import { clearAllDataFromLocalDb, insertDataIntoLocalDB, getDataFromLocalDb } from "../../../services/dbService";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";

export const setManualAdjustStockDataInDb = async (startDate: string, endDate: string, selectedDistributor ?: string) => {
  let supplierId = selectedDistributor ? selectedDistributor : JSON.parse(localStorage.authContext).user.loginId;
  try {
    const response = await ChannelkartNetworkGet(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getManualStockAdjustments?supplier=${
        supplierId
      }&startDate=${startDate}&endDate=${endDate}&minimalResponse=true`
    );
    await clearAllDataFromLocalDb("ManualStockAdjustData");
    await insertDataIntoLocalDB("ManualStockAdjustData", response.data["content"]);
  } catch (error) {
    console.log(error);
  }
};

export const approvalStatusMapping: orderStatusConfig[] = [
  {
    id: "approved",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Approved",
    dmsColor: "#F0FBFF",
  },
  {
    id: "rejected",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Rejected",
    dmsColor: "#FFFAF7",
  },
  {
    id: "under Review",
    dmsLabelColor: "#CC911F",
    dmsLabel: "Under Review",
    dmsColor: "#FFF9EF",
  },
  {
    id: "pending",
    dmsLabelColor: "#CC911F",
    dmsLabel: "Under Review",
    dmsColor: "#FFF9EF",
  },
  {
    id: "Partially Approved",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Partially Approved",
    dmsColor: "#F0FBFF",
  },
];

type NumericKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

export const stockTypeMapping: { [key: string]: NumericKeys<BatchAndExpiryManagementRowsData>[] } =
  {
    unsellable: ["pieceUnsellableQty", "caseUnsellableQty", "otherUnsellableQty"],
    returned: ["pieceReturnedQty", "caseReturnedQty", "otherReturnedQty"],
    available: ["pieceAvailableQty", "caseAvailableQty", "otherAvailableQty"],
    committed: ["pieceCommittedQty", "caseCommittedQty", "otherCommittedQty"],
  };

export const currentStockTypeOptions = ["Unsellable", "Returned", "Available", "Committed"];
export const reasons = [
  "Stock Good for Selling",
  "Damaged",
  "Expired",
  "Quality Inspection Failed",
  "Inventory Reconciliation",
];

export const manualStockAdjustmentMetadata = {
  columns: [
    {
      headerName: "Adjustment Date",
      mappedValue: "adjustmentDate",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "SKU Code",
      mappedValue: "skuCode",
      width: 220,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "SKU Name",
      mappedValue: "skuDescription",
      width: 310,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Updated Batch IDs",
      mappedValue: "batchIdCount",
      width: 130,
      headerAlign: "left",
      align: "left",
      headerParts: { top: "Updated", bottom: "Batch IDs" },
    },
    {
      headerName: "Stock From",
      mappedValue: "currentStock",
      width: 150,
      headerAlign: "left",
      align: "left",
      type: "stocks",
    },
    {
      headerName: "Stock To",
      mappedValue: "updatedStock",
      width: 150,
      headerAlign: "left",
      align: "left",
      type: "stocks",
    },
    {
      headerName: "",
      mappedValue: "eyeIcon",
      width: 10,
      headerAlign: "left",
      align: "center",
    },
  ],
  Actions: [],
};

export const stockAdjustSkuDetailsGridMetaData = {
  columns: [
    {
      headerName: "Batch ID",
      mappedValue: "batchId",
      width: 300,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Stock From",
      mappedValue: "currentStock",
      width: 300,
      headerAlign: "left",
      align: "left",
      type: "stocks",
    },
    {
      headerName: "Stock To",
      mappedValue: "updatedStock",
      width: 300,
      headerAlign: "left",
      align: "left",
      type: "stocks",
    },
    {
      headerName: "Stock Update Reason",
      mappedValue: "reasonForAdjustment",
      width: 300,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "",
      mappedValue: "skuDetailsEyeIcon",
      width: 10,
      headerAlign: "left",
      align: "center",
    },
  ],
  Actions: [],
};

export const stockAdjustSkuDetailsMetaData1: any[] = [
  {
    keys: "skuDescription",
    label: "SKU Name",
    index: 1,
  },
  {
    keys: "batchIdCount",
    label: "Batch IDs",
    index: 2,
  },
];

export const stockAdjustSkuDetailsMetaData2: any[] = [
  {
    keys: "adjustmentDate",
    label: "Adjustment Date",
    index: 1,
  },
];

// UOM Conversion Interface
export interface UOMConversionFactors {
  case2Piece: number;
  otherUnitToPieceQuantity: number;
  pieceToOtherUnitQuantity: number;
  uom: string;
}

// Function to get normalized quantity (following senior's getNqQuantity pattern)
export const getNqQuantity = async (qtyObject: any, productDetails?: any) => {
  try {
    let nq = 0;
    if (!productDetails) {
      const productData = (await getDataFromLocalDb("Product")).data[0].data;
      productDetails = productData[qtyObject.skuCode] ?? {};
    }
    if (productDetails) {
      if (qtyObject.totalCaseQty && productDetails.caseToPieceQuantity) {
        nq += qtyObject.totalCaseQty * productDetails.caseToPieceQuantity;
      }
      if (qtyObject.totalPieceQty) {
        nq += qtyObject.totalPieceQty;
      }
      if (qtyObject.totalOtherQty && productDetails.otherUnitToPieceQuantity) {
        nq += qtyObject.totalOtherQty * productDetails.otherUnitToPieceQuantity;
      }
    }
    return nq;
  } catch (e) {
    console.error("Error in getNqQuantity:", e);
  }
  return 0;
};

// Function to convert to max quantity (following senior's convertToMaxQty pattern)
export const convertToMaxQty = async (qtyObject: any, skuCode: string, uomHierarchy: string = "all") => {
  try {
    const productData = await getDataFromLocalDb("Product");
    const productDetails = productData.data[0].data[skuCode];
    if (productDetails) {
      let nq = await getNqQuantity(qtyObject, productDetails);
      qtyObject.totalCaseQty = 0;
      qtyObject.totalPieceQty = 0;
      qtyObject.totalOtherQty = 0;
      
      if (productDetails.caseToPieceQuantity && (uomHierarchy.includes("caseQty") || uomHierarchy === "all")) {
        qtyObject.totalCaseQty = Math.floor(nq / productDetails.caseToPieceQuantity);
        nq = nq % productDetails.caseToPieceQuantity;
      }
      if (productDetails.otherUnitToPieceQuantity && (uomHierarchy.includes("otherQty") || uomHierarchy === "all")) {
        qtyObject.totalOtherQty = Math.floor(nq / productDetails.otherUnitToPieceQuantity);
        nq = nq % productDetails.otherUnitToPieceQuantity;
      }
      qtyObject.totalPieceQty = nq;
    }
    return qtyObject;
  } catch (e) {
    console.error("Error in convertToMaxQty:", e);
    return qtyObject;
  }
};

// Helper function to normalize quantities for display using senior's pattern
export const normalizeQuantityDisplay = async (caseQty: number, pieceQty: number, otherQty: number, skuCode: string) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  
  if (!ConfigDictionary.uomConversion) {
    return { caseQty, pieceQty, otherQty };
  }

  try {
    const qtyObject = {
      totalCaseQty: caseQty,
      totalPieceQty: pieceQty,
      totalOtherQty: otherQty,
      skuCode: skuCode
    };

    const normalizedQty = await convertToMaxQty(qtyObject, skuCode, "all");
    
    return {
      caseQty: normalizedQty.totalCaseQty,
      pieceQty: normalizedQty.totalPieceQty,
      otherQty: normalizedQty.totalOtherQty
    };
  } catch (e) {
    console.error("Error in normalizeQuantityDisplay:", e);
    return { caseQty, pieceQty, otherQty };
  }
};
